import { useState, useEffect, useRef } from 'react';
import { alpha } from '@mui/material/styles';
import Body from "../components/layout/Body";
import { 
    Box, 
    Typography, 
    TextField, 
    Button, 
    Divider, 
    CircularProgress,
    Alert,
    Snackbar,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    ListItemText,
    FormHelperText,
    ListSubheader
} from "@mui/material";
import SaveIcon from '@mui/icons-material/Save';
import { useNavigate } from "react-router-dom";
import { isPsychologistProfileComplete, useAuth } from "../store/auth-context";
import useApiHook from "../hooks/useApiHook";
import cancelButtonSx from "../styles/cancelButtonSx";

const COUNTRY_CODES = [
    { code: '+1', name: 'Stati Uniti / Canada' },
    { code: '+7', name: 'Russia / Kazakistan' },
    { code: '+20', name: 'Egitto' },
    { code: '+27', name: 'Sudafrica' },
    { code: '+30', name: 'Grecia' },
    { code: '+31', name: 'Paesi Bassi' },
    { code: '+32', name: 'Belgio' },
    { code: '+33', name: 'Francia' },
    { code: '+34', name: 'Spagna' },
    { code: '+36', name: 'Ungheria' },
    { code: '+39', name: 'Italia' },
    { code: '+40', name: 'Romania' },
    { code: '+41', name: 'Svizzera' },
    { code: '+43', name: 'Austria' },
    { code: '+44', name: 'Regno Unito' },
    { code: '+45', name: 'Danimarca' },
    { code: '+46', name: 'Svezia' },
    { code: '+47', name: 'Norvegia' },
    { code: '+48', name: 'Polonia' },
    { code: '+49', name: 'Germania' },
    { code: '+51', name: 'Perù' },
    { code: '+52', name: 'Messico' },
    { code: '+53', name: 'Cuba' },
    { code: '+54', name: 'Argentina' },
    { code: '+55', name: 'Brasile' },
    { code: '+56', name: 'Cile' },
    { code: '+57', name: 'Colombia' },
    { code: '+58', name: 'Venezuela' },
    { code: '+60', name: 'Malesia' },
    { code: '+61', name: 'Australia' },
    { code: '+62', name: 'Indonesia' },
    { code: '+63', name: 'Filippine' },
    { code: '+64', name: 'Nuova Zelanda' },
    { code: '+65', name: 'Singapore' },
    { code: '+66', name: 'Thailandia' },
    { code: '+81', name: 'Giappone' },
    { code: '+82', name: 'Corea del Sud' },
    { code: '+84', name: 'Vietnam' },
    { code: '+86', name: 'Cina' },
    { code: '+90', name: 'Turchia' },
    { code: '+91', name: 'India' },
    { code: '+92', name: 'Pakistan' },
    { code: '+93', name: 'Afghanistan' },
    { code: '+94', name: 'Sri Lanka' },
    { code: '+95', name: 'Myanmar' },
    { code: '+98', name: 'Iran' },
    { code: '+212', name: 'Marocco' },
    { code: '+213', name: 'Algeria' },
    { code: '+216', name: 'Tunisia' },
    { code: '+218', name: 'Libia' },
    { code: '+220', name: 'Gambia' },
    { code: '+234', name: 'Nigeria' },
    { code: '+351', name: 'Portogallo' },
    { code: '+352', name: 'Lussemburgo' },
    { code: '+353', name: 'Irlanda' },
    { code: '+354', name: 'Islanda' },
    { code: '+355', name: 'Albania' },
    { code: '+356', name: 'Malta' },
    { code: '+357', name: 'Cipro' },
    { code: '+358', name: 'Finlandia' },
    { code: '+359', name: 'Bulgaria' },
    { code: '+370', name: 'Lituania' },
    { code: '+371', name: 'Lettonia' },
    { code: '+372', name: 'Estonia' },
    { code: '+373', name: 'Moldavia' },
    { code: '+374', name: 'Armenia' },
    { code: '+375', name: 'Bielorussia' },
    { code: '+376', name: 'Andorra' },
    { code: '+377', name: 'Monaco' },
    { code: '+378', name: 'San Marino' },
    { code: '+380', name: 'Ucraina' },
    { code: '+381', name: 'Serbia' },
    { code: '+382', name: 'Montenegro' },
    { code: '+385', name: 'Croazia' },
    { code: '+386', name: 'Slovenia' },
    { code: '+387', name: 'Bosnia-Erzegovina' },
    { code: '+389', name: 'Macedonia del Nord' },
    { code: '+420', name: 'Repubblica Ceca' },
    { code: '+421', name: 'Slovacchia' },
    { code: '+423', name: 'Liechtenstein' },
    { code: '+886', name: 'Taiwan' },
    { code: '+962', name: 'Giordania' },
    { code: '+963', name: 'Siria' },
    { code: '+964', name: 'Iraq' },
    { code: '+965', name: 'Kuwait' },
    { code: '+966', name: 'Arabia Saudita' },
    { code: '+967', name: 'Yemen' },
    { code: '+968', name: 'Oman' },
    { code: '+970', name: 'Palestina' },
    { code: '+971', name: 'Emirati Arabi Uniti' },
    { code: '+972', name: 'Israele' },
    { code: '+973', name: 'Bahrain' },
    { code: '+974', name: 'Qatar' },
    { code: '+975', name: 'Bhutan' },
    { code: '+976', name: 'Mongolia' },
    { code: '+977', name: 'Nepal' },
];

const approcciMenuItemSx = (theme) => ({
    '&&:hover': {
        backgroundColor: `${alpha(theme.palette.info.light, 0.28)} !important`
    },
    '&&.Mui-selected': {
        backgroundColor: `${alpha(theme.palette.info.main, 0.34)} !important`
    },
    '&&.Mui-selected:hover': {
        backgroundColor: `${alpha(theme.palette.info.main, 0.46)} !important`
    }
});

const TITOLI = ['Dott.', 'Dott.ssa'];
const PROFESSIONI = ['Psicologo', 'Psicoterapeuta'];
const ORIENTAMENTI_PRIMARI = [
    'Psicodinamico / Psicoanalitico',
    'Cognitivo-Comportamentale (CBT)',
    'Sistemico-Relazionale',
    'Psicoterapia Integrata (CMT, PIREP, TMI)',
    'CMT (Control Mastery theory)',
    'TMI (Terapia metacognitiva interpersonale)',
    'Umanistico-Esistenziale',
    'Analisi Transazionale',
    'Psicoterapia Breve Strategica',
    'Costruttivista',
    'Psicologia Analitica (Junghiana)',
    'Bioenergetico',
    'Analisi del Comportamento (ABA)'
];
const APPROCCI_INTEGRATIVI = [
    'EMDR (Terapia del Trauma)',
    'ACT (Acceptance and Commitment Therapy)',
    'Mindfulness / Protocolli MBSR',
    'Schema Therapy',
    'DBT (Terapia Dialettico Comportamentale)',
    'Ipnosi Clinica',
    'Psicoterapia Sensomotoria',
    'Terapia Focalizzata sulle Emozioni (EFT)',
    'Analisi Metacognitiva'
];

const compactFieldSx = {
    '& .MuiInputBase-root': {
        fontSize: '0.95rem'
    }
};

const compactSelectSx = {
    ...compactFieldSx,
    '& .MuiSelect-select': {
        minHeight: '1.25em'
    }
};

const SchedaPsicologo = () => {
    const navigate = useNavigate();
    const authCtx = useAuth();
    const user = authCtx.user;
    const isLoading = authCtx.isLoading;
    const isProfileComplete = authCtx.isProfileComplete;
    const { response: apiResponse, makeRequest, error: apiError } = useApiHook();

    const [formData, setFormData] = useState({
        name: '',
        lastname: '',
        prefissoTelefono: '',
        numeroTelefono: '',
        titolo: '',
        professione: '',
        orientamentoPrimario: '',
        approcciIntegrativi: []
    });

    const [saving, setSaving] = useState(false);
    const [isApprocciMenuOpen, setIsApprocciMenuOpen] = useState(false);
    const [draftApprocciIntegrativi, setDraftApprocciIntegrativi] = useState([]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const lastResponseRef = useRef(null);
    const lastErrorRef = useRef(null);
    const wasProfileCompleteRef = useRef(false);

    useEffect(() => {
        if (!isLoading && !user) {
            navigate('/auth');
        }
    }, [isLoading, user, navigate]);

    useEffect(() => {
        if (user) {
            const nextIsProfileComplete = isPsychologistProfileComplete(user);
            wasProfileCompleteRef.current = nextIsProfileComplete;
            const initialApprocciIntegrativi = Array.isArray(user.approcciIntegrativi) ? user.approcciIntegrativi : [];
            setFormData({
                name: user.name || '',
                lastname: user.lastname || '',
                prefissoTelefono: user.prefissoTelefono || '+39',
                numeroTelefono: user.numeroTelefono || '',
                titolo: user.titolo || '',
                professione: user.professione || '',
                orientamentoPrimario: user.orientamentoPrimario || user.orientamentoPsicologico || '',
                approcciIntegrativi: initialApprocciIntegrativi
            });
            setDraftApprocciIntegrativi(initialApprocciIntegrativi);
        }
    }, [user]);

    useEffect(() => {
        if (apiResponse && apiResponse.state === 'success' && apiResponse !== lastResponseRef.current) {
            lastResponseRef.current = apiResponse;
            authCtx.updateUser(apiResponse.data.user);
            const nextIsProfileComplete = isPsychologistProfileComplete(apiResponse.data.user);
            setSnackbar({
                open: true,
                message: 'Profilo aggiornato con successo!',
                severity: 'success'
            });
            setSaving(false);

            if (!wasProfileCompleteRef.current && nextIsProfileComplete) {
                navigate('/dashboard');
            }
        }
    }, [apiResponse]);

    useEffect(() => {
        if (apiError && apiError !== lastErrorRef.current) {
            lastErrorRef.current = apiError;
            setSnackbar({
                open: true,
                message: apiError.message || 'Errore durante il salvataggio',
                severity: 'error'
            });
            setSaving(false);
        }
    }, [apiError]);

    const isPhonePrefixMissing = !String(formData.prefissoTelefono || '').trim();
    const isPhoneNumberMissing = !String(formData.numeroTelefono || '').trim();
    const isTitoloMissing = !String(formData.titolo || '').trim();
    const isProfessioneMissing = !String(formData.professione || '').trim();
    const isOrientamentoPrimarioMissing = !formData.orientamentoPrimario;
    const isProfileIncomplete = (
        isPhonePrefixMissing ||
        isPhoneNumberMissing ||
        isTitoloMissing ||
        isProfessioneMissing ||
        isOrientamentoPrimarioMissing
    );

    const handleClose = () => {
        if (!isProfileComplete) {
            setSnackbar({
                open: true,
                message: "Completa obbligatoriamente la scheda dello psicologo per continuare.",
                severity: 'warning'
            });
            return;
        }

        navigate('/dashboard');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'approcciIntegrativi') {
            setDraftApprocciIntegrativi(Array.isArray(value) ? value : String(value).split(',').filter(Boolean));
            return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleOpenApprocciMenu = () => {
        setDraftApprocciIntegrativi(formData.approcciIntegrativi);
        setIsApprocciMenuOpen(true);
    };

    const handleCloseApprocciMenu = () => {
        setDraftApprocciIntegrativi(formData.approcciIntegrativi);
        setIsApprocciMenuOpen(false);
    };

    const handleConfirmApprocciMenu = () => {
        setFormData(prev => ({
            ...prev,
            approcciIntegrativi: draftApprocciIntegrativi
        }));
        setIsApprocciMenuOpen(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isPhonePrefixMissing || isPhoneNumberMissing || isTitoloMissing || isProfessioneMissing || isOrientamentoPrimarioMissing) {
            setSnackbar({
                open: true,
                message: "Compila tutti i campi obbligatori, incluso il numero di telefono.",
                severity: 'error'
            });
            return;
        }
        setSaving(true);
        await makeRequest('/auth/profile/update', 'post', {
            ...formData,
            orientamentoPsicologico: formData.orientamentoPrimario
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    if (isLoading || !user) {
        return (
            <Body mainElement={
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <CircularProgress />
                </Box>
            } />
        );
    }

    const mainContent = (
        <Box sx={{ maxWidth: 920, mx: 'auto' }}>
            <Box mb={{ xs: 2, md: 2.5 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.7rem', md: '2rem' } }}>
                    Scheda Psicologo
                </Typography>
                <Typography color="text.secondary" mt={0.75} sx={{ fontSize: '0.95rem' }}>
                    Gestisci le informazioni professionali e i dati di contatto del tuo account.
                </Typography>
                {!isProfileComplete ? (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        Prima di continuare devi completare obbligatoriamente la Scheda Psicologo. Il numero di telefono e` richiesto.
                    </Alert>
                ) : null}
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>Dati account</Typography>
                        <Box sx={{
                            display: 'grid',
                            gap: { xs: 1.5, md: 2 },
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }
                        }}>
                            <TextField
                                label="Nome"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                sx={compactFieldSx}
                            />

                            <TextField
                                label="Cognome"
                                name="lastname"
                                value={formData.lastname}
                                onChange={handleChange}
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                sx={compactFieldSx}
                            />

                            <TextField
                                label="Email"
                                value={user?.email || ''}
                                disabled
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                sx={{ ...compactFieldSx, gridColumn: { xs: '1', md: 'span 2' } }}
                            />
                        </Box>
                    </Box>
                </Paper>

                <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', p: { xs: 2, md: 2.5 }, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>Dati professionali</Typography>
                        <Box sx={{
                            display: 'grid',
                            gap: { xs: 1.5, md: 2 },
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }
                        }}>
                            <Box sx={{
                                gridColumn: { xs: '1', md: 'span 2' },
                                display: 'flex',
                                gap: 1.5,
                                flexDirection: { xs: 'column', sm: 'row' }
                            }}>
                                <FormControl
                                    size="small"
                                    required
                                    error={isPhonePrefixMissing}
                                    sx={{
                                        width: { xs: '100%', sm: '35%', md: '28%' }
                                    }}
                                >
                                    <InputLabel id="prefisso-label" shrink>
                                        Prefisso Telefono
                                    </InputLabel>
                                    <Select
                                        labelId="prefisso-label"
                                        name="prefissoTelefono"
                                        value={formData.prefissoTelefono}
                                        onChange={handleChange}
                                        label="Prefisso Telefono"
                                        notched
                                        sx={compactSelectSx}
                                    >
                                        {COUNTRY_CODES.map((country) => (
                                            <MenuItem key={country.code} value={country.code}>
                                                {country.code} ({country.name})
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {isPhonePrefixMissing ? <FormHelperText>Campo obbligatorio</FormHelperText> : null}
                                </FormControl>

                                <TextField
                                    label="Numero Telefono"
                                    name="numeroTelefono"
                                    value={formData.numeroTelefono}
                                    onChange={handleChange}
                                    size="small"
                                    fullWidth
                                    required
                                    error={isPhoneNumberMissing}
                                    helperText={isPhoneNumberMissing ? "Campo obbligatorio" : ""}
                                    InputLabelProps={{ shrink: true }}
                                    placeholder="es. 3331234567"
                                    sx={{ ...compactFieldSx, flex: 1 }}
                                />
                            </Box>

                            <FormControl fullWidth size="small" required error={isTitoloMissing}>
                                <InputLabel id="titolo-label" shrink>
                                    Titolo
                                </InputLabel>
                                <Select
                                    labelId="titolo-label"
                                    name="titolo"
                                    value={formData.titolo}
                                    onChange={handleChange}
                                    label="Titolo"
                                    displayEmpty
                                    notched
                                    sx={compactSelectSx}
                                >
                                    <MenuItem value="">
                                        <em>Seleziona titolo</em>
                                    </MenuItem>
                                    {TITOLI.map((titolo) => (
                                        <MenuItem key={titolo} value={titolo}>
                                            {titolo}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {isTitoloMissing ? <FormHelperText>Campo obbligatorio</FormHelperText> : null}
                            </FormControl>

                            <FormControl fullWidth size="small" required error={isProfessioneMissing}>
                                <InputLabel id="professione-label" shrink>
                                    Professione
                                </InputLabel>
                                <Select
                                    labelId="professione-label"
                                    name="professione"
                                    value={formData.professione}
                                    onChange={handleChange}
                                    label="Professione"
                                    displayEmpty
                                    notched
                                    sx={compactSelectSx}
                                >
                                    <MenuItem value="">
                                        <em>Seleziona professione</em>
                                    </MenuItem>
                                    {PROFESSIONI.map((prof) => (
                                        <MenuItem key={prof} value={prof}>
                                            {prof}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {isProfessioneMissing ? <FormHelperText>Campo obbligatorio</FormHelperText> : null}
                            </FormControl>

                            <FormControl
                                fullWidth
                                size="small"
                                required
                                error={isOrientamentoPrimarioMissing}
                            >
                                <InputLabel id="orientamento-primario-label" shrink>
                                    Orientamento Primario
                                </InputLabel>
                                <Select
                                    labelId="orientamento-primario-label"
                                    name="orientamentoPrimario"
                                    value={formData.orientamentoPrimario}
                                    onChange={handleChange}
                                    label="Orientamento Primario"
                                    displayEmpty
                                    notched
                                    sx={compactSelectSx}
                                >
                                    <MenuItem value="">
                                        <em>Seleziona orientamento primario</em>
                                    </MenuItem>
                                    {ORIENTAMENTI_PRIMARI.map((orientamento) => (
                                        <MenuItem key={orientamento} value={orientamento}>
                                            {orientamento}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {isOrientamentoPrimarioMissing && (
                                    <FormHelperText>Campo obbligatorio</FormHelperText>
                                )}
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel id="approcci-integrativi-label" shrink>
                                    Approcci e Tecniche Integrative
                                </InputLabel>
                                <Select
                                    labelId="approcci-integrativi-label"
                                    name="approcciIntegrativi"
                                    multiple
                                    open={isApprocciMenuOpen}
                                    onOpen={handleOpenApprocciMenu}
                                    onClose={handleCloseApprocciMenu}
                                    value={isApprocciMenuOpen ? draftApprocciIntegrativi : formData.approcciIntegrativi}
                                    onChange={handleChange}
                                    label="Approcci e Tecniche Integrative"
                                    renderValue={(selected) => (
                                        Array.isArray(selected) && selected.length > 0
                                            ? selected.join(', ')
                                            : 'Nessun approccio integrativo selezionato'
                                    )}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                maxHeight: 320,
                                                width: 400,
                                                maxWidth: 'calc(100vw - 32px)'
                                            }
                                        },
                                        MenuListProps: {
                                            sx: {
                                                '& .MuiMenuItem-root': approcciMenuItemSx
                                            }
                                        }
                                    }}
                                    notched
                                    sx={compactSelectSx}
                                >
                                    {APPROCCI_INTEGRATIVI.map((approccio) => (
                                        <MenuItem
                                            key={approccio}
                                            value={approccio}
                                            selected={draftApprocciIntegrativi.includes(approccio)}
                                            sx={approcciMenuItemSx}
                                        >
                                            <Checkbox checked={draftApprocciIntegrativi.includes(approccio)} />
                                            <ListItemText primary={approccio} />
                                        </MenuItem>
                                    ))}
                                    <ListSubheader
                                        disableSticky
                                        sx={{
                                            bgcolor: 'background.paper',
                                            borderTop: '1px solid',
                                            borderColor: 'divider',
                                            py: 1.5
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                type="button"
                                                variant="contained"
                                                size="small"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    handleConfirmApprocciMenu();
                                                }}
                                            >
                                                OK
                                            </Button>
                                        </Box>
                                    </ListSubheader>
                                </Select>
                                <FormHelperText>Campo facoltativo</FormHelperText>
                            </FormControl>
                        </Box>
                    </Box>
                </Paper>

                <Divider sx={{ my: 0.5 }} />

                <Box sx={{
                    display: 'flex',
                    justifyContent: { xs: 'stretch', md: 'flex-end' },
                    gap: 1.5,
                    flexDirection: { xs: 'column', sm: 'row' }
                }}>
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={handleClose}
                        size="small"
                        disabled={!isProfileComplete}
                        sx={cancelButtonSx}
                    >
                        Annulla
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        disabled={saving || isProfileIncomplete}
                        size="small"
                        sx={{
                            minWidth: { xs: '100%', sm: '190px' },
                            minHeight: '40px'
                        }}
                    >
                        {saving ? 'Salvataggio...' : 'Salva modifiche'}
                    </Button>
                </Box>
            </Box>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSnackbar} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );

    return <Body mainElement={mainContent} />;
};

export default SchedaPsicologo;
